import { createFileRoute, Link } from "@tanstack/react-router";

export const Route = createFileRoute("/about")({
  head: () => ({
    meta: [
      { title: "House, HOWDEN" },
      {
        name: "description",
        content:
          "HOWDEN sources certified lab-grown diamonds and offers them loose, by the carat. Cultivated, independently graded, fully traceable.",
      },
      { property: "og:title", content: "House, HOWDEN" },
      {
        property: "og:description",
        content:
          "HOWDEN sources certified lab-grown diamonds and offers them loose, by the carat. Cultivated, independently graded, fully traceable.",
      },
      { property: "og:url", content: "https://howdendiamonds.com/about" },
    ],
    links: [{ rel: "canonical", href: "https://howdendiamonds.com/about" }],
  }),
  component: AboutPage,
});

const STANDARDS = [
  {
    title: "Cultivation",
    body: "Grown atom by atom over weeks in a controlled reactor — chemically and optically identical to a mined stone.",
  },
  {
    title: "Cut",
    body: "Rough is planned and polished by master cutters; we list only stones that read with life and precision.",
  },
  {
    title: "Grading",
    body: "Every stone is independently graded by IGI or GIA, with the report shipped alongside the diamond.",
  },
  {
    title: "Traceability",
    body: "Each diamond carries a documented origin, from reactor to certificate to your hand.",
  },
];

function AboutPage() {
  return (
    <div className="mx-auto max-w-4xl px-6 py-20 lg:px-12 lg:py-32">
      <p className="text-xs uppercase tracking-luxe text-muted-foreground">
        The House
      </p>
      <h1 className="mt-6 font-serif text-5xl font-light leading-[1.05] lg:text-7xl">
        Cultivated stones, <em className="italic text-accent-foreground/80">loose</em> by the carat.
      </h1>

      <div className="mt-16 grid gap-12 lg:grid-cols-3">
        <div className="lg:col-span-2 space-y-6 text-base leading-relaxed text-muted-foreground">
          <p>
            HOWDEN is a house for loose diamonds. We do not make jewelry. We
            do not run a factory. We source certified lab-grown stones, one
            at a time, and offer them as they were graded — by the carat,
            with the report attached.
          </p>
          <p>
            Each diamond is grown in a controlled environment using the same
            elemental process that occurs beneath the earth, only faster and
            with greater precision. The result is chemically and optically
            identical to a mined stone, and entirely traceable.
          </p>
          <p>
            Every rough is planned, cut and polished by trained hands, then
            graded independently by IGI or GIA. We list only stones we would
            choose to set ourselves; everything else stays in the rough.
          </p>
        </div>
        <aside className="space-y-8 border-l border-hairline pl-8 text-sm">
          <div>
            <p className="text-xs uppercase tracking-luxe text-muted-foreground">
              Founded
            </p>
            <p className="mt-2 font-serif text-2xl">2025</p>
          </div>
          <div>
            <p className="text-xs uppercase tracking-luxe text-muted-foreground">
              Sold
            </p>
            <p className="mt-2 font-serif text-2xl">By the carat</p>
          </div>
          <div>
            <p className="text-xs uppercase tracking-luxe text-muted-foreground">
              Certification
            </p>
            <p className="mt-2 font-serif text-2xl">IGI · GIA</p>
          </div>
        </aside>
      </div>

      <div className="mt-24 border-t border-hairline pt-12">
        <p className="text-xs uppercase tracking-luxe text-muted-foreground">
          The standard
        </p>
        <h2 className="mt-4 font-serif text-3xl font-light">
          Four measures. One stone.
        </h2>
        <ul className="mt-10 grid gap-px bg-hairline sm:grid-cols-2 lg:grid-cols-4">
          {STANDARDS.map((c) => (
            <li key={c.title} className="bg-background p-6">
              <p className="font-serif text-xl">{c.title}</p>
              <p className="mt-2 text-xs uppercase tracking-luxe text-muted-foreground">
                {c.body}
              </p>
            </li>
          ))}
        </ul>
      </div>

      <div className="mt-24 border-t border-hairline pt-12">
        <h2 className="font-serif text-3xl font-light">A standard, not a style.</h2>
        <p className="mt-6 max-w-2xl text-base leading-relaxed text-muted-foreground">
          We list diamonds by a single measure: would we set this stone
          ourselves? If the answer is anything other than yes, it does not
          enter the vault.
        </p>
        <Link
          to="/shop"
          className="mt-10 inline-block border-b border-foreground pb-1 text-xs uppercase tracking-luxe"
        >
          View the diamonds
        </Link>
      </div>
    </div>
  );
}
